import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Typography from '../../components/Typography';
import CustomButton from '../../components/CustomButton';
import SelectInvestment from '../../components/investing/SelectInvestment';
import PromissoryNote from '../../components/investing/PromissoryNote';
import ContractSigning from '../../components/investing/ContractSigning';
import WireTransfer from '../../components/investing/WireTransfer';
import group1 from '../../images/Group1.png';
import group from '../../images/Group.png';
import lightning from '../../images/lightning.png';
import verticalline from '../../images/vertical_line.png';
import CustomStepper from '../../components/CustomStepper';
import CustomModal from '../../components/CustomModal';

const plans = [
    { name: 'Plan 1', term: 1, annualROI: 20, minInvestment: 50000 },
    { name: 'Plan 2', term: 2.5, annualROI: 25, minInvestment: 50000 },
    { name: 'Plan 3', term: 2.5, annualROI: 30, minInvestment: 50000 },
];

const wireTransferDetails = {
    recipientName: 'Ash Capital LLC',
    bankName: 'Chase Bank',
    bankAddress: '801 Brickell Ave #800, Miami FL 33131',
    routingNumber: '021000021',
    accountNumber: '788650817',
    swiftCode: 'CHASUS33',
    reference: '',
};

const StartInvesting = () => {
    const [user, setUser] = useState(null);
    const [currentStep, setCurrentStep] = useState(1);
    const [selectedPlan, setSelectedPlan] = useState(plans[0]);
    const [investmentAmount, setInvestmentAmount] = useState('');
    const [promissoryNote, setPromissoryNote] = useState('');
    const [contractSigned, setContractSigned] = useState(false);
    const [wireTransferConfirmed, setWireTransferConfirmed] = useState(false);
    const [signature, setSignature] = useState(null);
    const [totalReturn, setTotalReturn] = useState(0);
    const [errorTitle, setErrorTitle] = useState('OH SNAP!');
    const [errorCloseText, setErrorCloseText] = useState('Try Again');
    const [error, setError] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [errorProfileMgg, setErrorProfileMgg] = useState('');
    const [showLoadingModal, setShowLoadingModal] = useState(false);
    const [investmentData, setInvestmentData] = useState({}); // Collect all data here
    const navigate = useNavigate();

    // Fetch user data
    useEffect(() => {
        const fetchUser = async () => {
            const token = localStorage.getItem('token');
            const userInfo = localStorage.getItem('userInfo');
            setUser(JSON.parse(userInfo));
            if (!token || !userInfo) {
                navigate('/login');
                return;
            }
        };
        fetchUser();
    }, [navigate]);

    const handleSubmitStep = async () => {
        try {
            // Validate and collect data for each step, but don't save until Step 4
            if (currentStep === 1) {
                if (!selectedPlan || !investmentAmount) {
                    setError('Please select a plan and enter a valid investment amount');
                    return;
                }
                if (!user?.email) {
                    setErrorProfileMgg('Please provide your email address in your settings before proceeding with the investment.');
                    setShowProfileModal(true);
                    return;
                }
                if (!user?.address) {
                    setErrorProfileMgg('Please provide your address in your settings before proceeding with the investment.');
                    setShowProfileModal(true);
                    return;
                }
                if (!user?.name) {
                    setErrorProfileMgg('Please provide your name in your settings before proceeding with the investment.');
                    setShowProfileModal(true);
                    return;
                }
                if (!user?.phone) {
                    setErrorProfileMgg('Please provide your phone number in your settings before proceeding with the investment.');
                    setShowProfileModal(true);
                    return;
                }
                if (!user?.license) {
                    setErrorProfileMgg('Please provide your license in your settings before proceeding with the investment.');
                    setShowProfileModal(true);
                    return;
                }

                setInvestmentData({
                    ...investmentData,
                    plan: selectedPlan.name,
                    initialInvestment: parseFloat(investmentAmount),
                    investorName: user?.name,
                });
                setCurrentStep(currentStep + 1);
            } else if (currentStep === 2) {
                if (!promissoryNote) {
                    setErrorTitle('READ OUR TERMS!');
                    setErrorCloseText('READ TERMS');
                    setError('Before Moving Forward! Please review and confirm our Terms & Conditions below.');
                    return;
                }
                setInvestmentData({
                    ...investmentData,
                    investorName: user?.name,
                    promissoryNote: promissoryNote,
                });
                setCurrentStep(currentStep + 1);
            } else if (currentStep === 3) {
                if (!signature) {
                    setError('Please provide your e-signature');
                    return;
                }
                setInvestmentData({
                    ...investmentData,
                    contractSigned: true,
                    signature: signature,
                });
                setShowConfirmModal(true); // Show confirmation modal for Step 3
                return; // Wait for confirmation
            } else if (currentStep === 4) {
                if (!wireTransferConfirmed) {
                    setError('Please confirm wire transfer details');
                    return;
                }
                const finalData = {
                    ...investmentData,
                    wireTransferConfirmed: true,
                    wireTransferDetails: wireTransferDetails,
                };
                console.log(finalData);
                // Save all collected data to the backend in Step 4
                const token = localStorage.getItem('token');
                await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/investment`, finalData, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to proceed');
            setShowLoadingModal(false);
        }
    };

    const handleConfirm = async () => {
        setShowConfirmModal(false);
        const pendingData = {
            ...investmentData,
            status: 'pending', // Set status to pending for admin approval
        };

        const token = localStorage.getItem('token');
        setShowLoadingModal(true);
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/investment`, pendingData, {
            headers: { Authorization: `Bearer ${token}` },
        }).finally(() => {
            setTimeout(() => {
                setShowLoadingModal(false)
                navigate('/waiting');
            }, 2000);
        });
        // setCurrentStep(currentStep + 1); // Proceed to Step 4 after confirmation
    };

    const handleLoadingOkay = () => {
        setShowLoadingModal(false);
        navigate('/waiting'); // Redirect to waiting page after Step 4
    };

    return (
        <div className="lg:py-[40px] md:py-[35px] py-[30px] lg:px-[93px] px-0 w-full h-full min-h-screen bg-[#000000]">
            {error && (
                <CustomModal
                    type="error"
                    title={errorTitle}
                    message={error}
                    colseText={errorCloseText}
                    onClose={() => setError(null)}
                />
            )}
            {showConfirmModal && (
                <CustomModal
                    type="confirm"
                    title="READY TO SEND?"
                    message="Are you sure you're ready to submit this investment inquiry to Ash Capital? Click 'Confirm' to proceed."
                    onConfirm={handleConfirm}
                    onClose={() => setShowConfirmModal(false)}
                    colseText="Confirm"
                    cancelText="Cancel"
                />
            )}
            {showProfileModal && (
                <CustomModal
                type="error"
                title="COMPLETE YOUR PROFILE"
                message={errorProfileMgg}
                onConfirm={() => navigate('/settings')}
                onClose={() => setShowProfileModal(false)}
                colseText="Go to Settings"
                cancelText="Cancel"
                />
            )}
            {showLoadingModal && (
                <CustomModal
                    type="loading"
                    title="Please wait"
                    confirmButton={false}
                    onConfirm={handleLoadingOkay}
                    message="Please wait while we submit your investment inquiry to Ash Capital..."
                    onClose={() => setShowLoadingModal(false)}
                    colseText="Okay"
                />
            )}
            <div className="max-w-6xl mx-auto sm:p-6 px-4">
                <div className="lg:flex hidden items-center justify-center">
                    <div className="flex relative justify-center items-center">
                        <img src={group1} alt="group1" className="" />
                        <img src={lightning} alt="lightning" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
                    </div>
                    <img src={verticalline} alt="verticalline" className="ml-[19.26px]" />
                    <img src={group} alt="group" className="ml-[16.53px]" />
                </div>
                <div className='lg:hidden flex items-center justify-center'>
                    <svg xmlns="http://www.w3.org/2000/svg" width="169" height="28" viewBox="0 0 169 28" fill="none">
                        <g clipPath="url(#clip0_4613_6243)">
                            <path d="M78.0256 15.1229C77.7885 15.8099 77.3878 16.3481 76.8246 16.7377C76.2613 17.1273 75.5953 17.3237 74.8266 17.3237C73.8051 17.3237 72.9675 16.9941 72.3106 16.3359C71.6525 15.6778 71.3263 14.8343 71.3263 13.8055C71.3263 12.7767 71.6525 11.9343 72.3106 11.2751C72.9686 10.617 73.8051 10.2873 74.8266 10.2873C75.5739 10.2873 76.224 10.4682 76.7828 10.8378C77.3381 11.203 77.7389 11.709 77.9793 12.3561H79.1431C78.8778 11.3883 78.3586 10.6214 77.5786 10.0465C76.7986 9.47162 75.8798 9.1875 74.8255 9.1875C73.9349 9.1875 73.1346 9.38394 72.4234 9.77793C71.7123 10.1719 71.1536 10.7213 70.7529 11.4249C70.3521 12.1275 70.1523 12.9232 70.1523 13.8066C70.1523 14.6901 70.3521 15.4814 70.7529 16.1883C71.1536 16.8909 71.7123 17.4413 72.4234 17.8353C73.1346 18.2293 73.9349 18.4258 74.8255 18.4258C75.9035 18.4258 76.8347 18.1283 77.6192 17.5346C78.4037 16.9408 78.9263 16.1373 79.1882 15.1251H78.0244L78.0256 15.1229Z" fill="white" />
                            <path d="M92.237 9.32031H91.1669L87.4453 18.2844H88.6587L89.5494 16.1402H93.9279L94.8309 18.2844H96.0692L92.2381 9.32031H92.237ZM89.9738 15.1081L91.7223 10.8708L93.5035 15.1081H89.9738Z" fill="white" />
                            <path d="M110.773 10.1438C110.192 9.59333 109.473 9.32031 108.615 9.32031H105.348V18.2844H106.483V15.0637H108.615C109.478 15.0637 110.2 14.7907 110.776 14.2446C111.352 13.6986 111.638 13.0161 111.638 12.1959C111.638 11.3757 111.348 10.6898 110.773 10.1438ZM109.93 13.47C109.57 13.8074 109.11 13.9761 108.55 13.9761H106.483V10.4102H108.55C109.11 10.4102 109.572 10.5789 109.93 10.9207C110.289 11.2581 110.47 11.6843 110.47 12.1981C110.47 12.712 110.291 13.1337 109.93 13.4711V13.47Z" fill="white" />
                            <path d="M121.098 9.32031V18.2844H122.238V9.32031H121.098Z" fill="white" />
                            <path d="M131.566 9.32031V10.3769H134.336V18.2844H135.48V10.3769H138.253V9.32031H131.566Z" fill="white" />
                            <path d="M150.506 9.32031H149.436L145.711 18.2844H146.928L147.814 16.1402H152.197L153.1 18.2844H154.338L150.507 9.32031H150.506ZM148.243 15.1081L149.991 10.8708L151.768 15.1081H148.243Z" fill="white" />
                            <path d="M164.753 17.2323V9.32031H163.617V18.2844H168.755V17.2323H164.753Z" fill="white" />
                            <path d="M9.46028 7.29688H6.21731L0 22.9122H3.63128L4.71829 19.9445H11.0496L12.1603 22.9122H15.8819L9.46028 7.29688ZM5.80869 17.0002L7.87209 11.421L9.9592 17.0002H5.80869Z" fill="white" />
                            <path d="M42.6339 7.29688V13.3177H36.6705V7.29688H33.2188V22.9122H36.6705V16.4619H42.6339V22.9122H46.0608V7.29688H42.6339Z" fill="white" />
                            <path d="M59.6257 0H58.3242V27.616H59.6257V0Z" fill="white" />
                            <path d="M23.7237 12.8003L20.5913 11.9235C23.1491 10.0801 25.7882 8.19892 28.3472 6.35659C26.5163 8.12678 24.6324 9.97354 22.8026 11.7437C22.8071 11.7593 22.8128 11.7748 22.8173 11.7892C23.9449 12.1244 25.0715 12.4596 26.2127 12.7981C26.8775 11.6971 27.539 10.6028 28.1993 9.50741C28.8596 8.4109 29.5222 7.31549 30.1803 6.21675C30.5076 5.67072 30.8452 5.11136 31.1804 4.55089C31.5168 3.99154 31.852 3.43107 32.176 2.88281C29.4319 4.72847 26.6551 6.5941 23.8795 8.45973C21.1027 10.3243 18.327 12.1899 15.5818 14.0366C18.3372 14.7869 21.1557 15.546 23.9438 16.3051C22.9448 17.3107 21.9278 18.3273 20.9119 19.3417C19.896 20.356 18.8824 21.3682 17.8924 22.3648C18.204 21.9498 18.5765 21.4415 18.888 21.0264C19.2052 20.6035 19.5224 20.1807 19.8396 19.7578C20.1534 19.3394 20.4706 18.9221 20.7798 18.5004C21.0869 18.0831 21.3995 17.6403 21.6942 17.213C20.1218 18.9987 18.5302 20.8022 16.9364 22.6068C15.3437 24.4103 13.7509 26.2149 12.1797 28.0006C18.6363 23.7222 25.1764 19.4049 31.6624 15.1076C31.6929 15.0877 31.7233 15.0677 31.7538 15.0466L23.7248 12.7992H23.7226L23.7237 12.8003Z" fill="#DF0F11" />
                        </g>
                        <defs>
                            <clipPath id="clip0_4613_6243">
                                <rect width="168.757" height="28" fill="white" />
                            </clipPath>
                        </defs>
                    </svg>
                </div>
                <Typography
                    text=""
                    color="#FFF"
                    size="14px"
                    weight={400}
                    className="text-center mt-6 mb-10"
                />
                <div className="flex relative flex-col items-center overflow-hidden justify-center lg:mb-10 md:mb-7 mb-3">
                    <CustomStepper currentStep={currentStep} />
                </div>

                {currentStep === 1 && (
                    <SelectInvestment
                        selectedPlan={selectedPlan}
                        setSelectedPlan={setSelectedPlan}
                        investmentAmount={investmentAmount}
                        setInvestmentAmount={setInvestmentAmount}
                        totalReturn={totalReturn}
                        setTotalReturn={setTotalReturn}
                        error={error}
                        setError={setError}
                    />
                )}

                {currentStep === 2 && (
                    <PromissoryNote
                        investorName={user?.name}
                        promissoryNote={promissoryNote}
                        setPromissoryNote={setPromissoryNote}
                        investmentAmount={investmentAmount}
                        selectedPlan={selectedPlan}
                        error={error}
                    />
                )}

                {currentStep === 3 && (
                    <ContractSigning
                        contractSigned={contractSigned}
                        setContractSigned={setContractSigned}
                        signature={signature}
                        user={user}
                        investment={investmentData}
                        setSignature={setSignature}
                        error={error}
                    />
                )}

                {currentStep === 4 && (
                    <WireTransfer
                        wireTransferConfirmed={wireTransferConfirmed}
                        wireTransferDetails={wireTransferDetails}
                        setWireTransferConfirmed={setWireTransferConfirmed}
                        error={error}
                    />
                )}

                <div className={`flex md:flex-row flex-col justify-between items-center md:gap-[57px] gap-6 mt-6 ${currentStep !== 1 && 'md:px-28'}`}>
                    {currentStep === 1 && (
                        <div className="flex">
                            {/* <Typography text="Minimum Investment Amount: $50,000" color="#FFF" size="24px" lineheight={'32px'} weight={400} transform="uppercase" family="Supply" /> */}
                        </div>
                    )}
                    {currentStep > 1 && (
                        <CustomButton
                            text="PREVIOUS"
                            bgColor="#000000"
                            textColor="#FFFFFF"
                            strock={'rgba(255, 255, 255, 0.40)'}
                            className="text-white md:w-[209px] px-6 py-2"
                            onClick={() => setCurrentStep(currentStep - 1)}
                        />
                    )}
                    <CustomButton
                        text={currentStep === 4 ? 'COMPLETE' : 'PROCEED'}
                        bgColor="#DF0F11"
                        textColor="#FFFFFF"
                        className="text-white md:w-[209px] px-6 py-2"
                        onClick={handleSubmitStep}
                    />
                </div>
            </div>
        </div>
    );
};

export default StartInvesting;